// ═══════════════════════════════════════════════════════════════════════════════
// Protected Route Component
// ═══════════════════════════════════════════════════════════════════════════════

import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import type { UserRole } from '@fdh/shared';
import { getPostAuthHomePath } from '../lib/authRedirect';

type ProtectedRouteProps = {
  children: React.ReactNode;
  requiredRole?: UserRole;
  skipConsent?: boolean;
};

export const ProtectedRoute = ({
  children,
  requiredRole,
  skipConsent = false,
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { t } = useLanguage();

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-gray-50"
        role="status"
        aria-label={t('protected.loadingAria')}
      >
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (requiredRole && user && user.role !== requiredRole) {
    return <Navigate to={getPostAuthHomePath(user)} replace />;
  }

  // Redirect to consent page if user has not consented (consentedAt is empty)
  if (!skipConsent && user && !user.consentedAt) {
    return <Navigate to="/consent" replace />;
  }

  return <>{children}</>;
};
